import { IconProgressCheck } from "@tabler/icons-react";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { CheckCircle, Database, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { userSelector } from "@/redux/user/user.selector";
import { clientSelector } from "@/redux/clients/clients.selector";
import { useSelector, useDispatch } from "react-redux";
import { updateDb } from "@/redux/clients/clients.action";
import { AppDispatch } from "@/redux/store";
import { useEffect, useRef } from "react";

export function NavSecondary({
  ...props
}: React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const { userInfo } = useSelector(userSelector).ui;
  const { updateDbLoading } = useSelector(clientSelector).ui;
  const dispatch = useDispatch<AppDispatch>();
  const loadingToastId = useRef<string | number | null>(null);

  useEffect(() => {
    if (updateDbLoading && !loadingToastId.current) {
      // Show persistent DB update toast
      loadingToastId.current = toast.custom(
        () => (
          <div className="flex items-center gap-3 p-4 rounded-lg shadow-lg min-w-[20rem] bg-blue-50 border border-blue-200">
            <div className="relative">
              <Database className="text-blue-600 h-5 w-5" />
              <Loader2 className="absolute -top-1 -right-1 h-3 w-3 animate-spin text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-[13px] text-blue-800 font-[var(--font-inter)]">
                Actualizando Base de Datos
              </p>
              <p className="text-[11px] text-blue-600 font-[var(--font-inter)]">
                Por favor espere, no cierre la aplicación
              </p>
            </div>
          </div>
        ),
        {
          duration: Infinity,
        },
      );
    } else if (!updateDbLoading && loadingToastId.current) {
      // Dismiss the loading toast when done
      toast.dismiss(loadingToastId.current);
      loadingToastId.current = null;

      // Also dismiss all toasts as fallback
      toast.dismiss();
    }
  }, [updateDbLoading]);

  const handleNewDate = () => {
    const date = new Date();

    const datePart = date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit",
    });

    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const finalFormat = `${datePart} at ${timePart}`;
    return finalFormat;
  };

  const handleUpdateDatabase = async () => {
    try {
      await dispatch(updateDb()).unwrap();
      const finalFormat = handleNewDate();
      toast.custom(() => (
        <div className="flex items-center gap-3 p-4 rounded shadow-md min-w-[25rem] bg-[#F0FFF4] font-[12px] font-medium">
          <CheckCircle className="text-[#1F6B45]" />
          <div>
            <p className="font-semibold text-[12px] font-[var(--font-inter)]">
              Base de datos actualizada
            </p>
            <p className="text-[10px] font-[var(--font-inter)]">
              {finalFormat}
            </p>
          </div>
        </div>
      ));
    } catch (error) {
      toast.error("Error al actualizar la base de datos");
    }
  };

  return (
    <>
      {userInfo?.role === "Administrador" && (
        <SidebarGroup {...props}>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleUpdateDatabase}
                  disabled={updateDbLoading}
                  className={
                    updateDbLoading
                      ? "opacity-75 cursor-not-allowed bg-blue-50 border border-blue-200"
                      : ""
                  }
                >
                  {updateDbLoading ? (
                    <div className="relative">
                      <Database className="h-4 w-4 text-blue-600" />
                      <Loader2 className="absolute -top-1 -right-1 h-2 w-2 animate-spin text-blue-600" />
                    </div>
                  ) : (
                    <IconProgressCheck className="h-4 w-4" />
                  )}
                  <span
                    className={`text-[14px] font-[var(--font-inter)] ${
                      updateDbLoading ? "text-blue-700 font-medium" : ""
                    }`}
                  >
                    {updateDbLoading
                      ? "Actualizando Base de Datos..."
                      : "Actualizar base de datos"}
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </>
  );
}
